import { useCallback, useContext, useEffect, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntegrationRefresh } from '../../../../api/integrations-client/integrations-client.hooks';
import type { IntegrationEntry } from '../../../../api/integrations-client/integrations-client.type';
import { Button } from '../../../../components/button/button';
import { UserContext } from '../../../../contexts';
import { graphSunglow, redBase, secondaryBase } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import {
  getIntegrationStatus,
  getRefreshMessage,
  setIntegrationForReauth,
} from '../../../integrations/integrations.helpers';
import { useIntegrationAuth } from '../../../integrations/integrations.hooks';
import { IntegrationStatus } from '../../../integrations/integrations.type';
import { toast } from '../../../notifications';

type Props = {
  integrationEntry: IntegrationEntry;
  refreshStatus?: number;
  onRefreshSuccess?: (status: number) => void;
};

export function DataSourcesActionButton({ integrationEntry, refreshStatus, onRefreshSuccess }: Props) {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const [refreshIntegrationId, setRefreshIntegrationId] = useState<string | null>(null);

  const { authenticate } = useIntegrationAuth(integrationEntry.integration_type);

  const { data, query } = useIntegrationRefresh({
    queryKey: ['integration-refresh', refreshIntegrationId] as const,
    enabled: !!refreshIntegrationId,
  });

  const status = getIntegrationStatus(integrationEntry);
  const isExpired = status === IntegrationStatus.Expired;
  const isIntegrator = integrationEntry.user_name === user?.name;

  const getText = useCallback(() => {
    if (isExpired) {
      return isIntegrator
        ? t('data_management.actions.re_authenticate')
        : t('data_management.actions.contact_integrator');
    }

    return t('data_management.actions.refresh');
  }, [isExpired, isIntegrator, t]);

  const onClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();

      if (isExpired && isIntegrator) {
        setIntegrationForReauth(integrationEntry.id);
        return authenticate();
      }

      setRefreshIntegrationId(integrationEntry.id);
    },
    [isExpired, isIntegrator, integrationEntry.id, authenticate],
  );

  useEffect(() => {
    if (query.isFetched && refreshIntegrationId) {
      setRefreshIntegrationId(null);

      if (query.isError) {
        toast.error(t('data_management.actions.refresh_error'));
      } else if (data && typeof data.status === 'number') {
        toast.success(t('data_management.actions.refresh_success'));
        onRefreshSuccess?.(data.status);
      }
    }
  }, [data, query.isFetched, query.isError, refreshIntegrationId, onRefreshSuccess, t]);

  const { projects: _, ...integrationData } = integrationEntry;
  const projectForRefresh = {
    ...integrationData,
    name: integrationEntry.projects?.[0]?.name || t('data_management.unknown_project'),
    project_id: integrationEntry.projects?.[0]?.id || '',
    subprojects: integrationEntry.subprojects,
    status,
  };

  const refreshMessage = getRefreshMessage(projectForRefresh, refreshStatus);

  if (refreshMessage) {
    return <Text color={secondaryBase}>{refreshMessage}</Text>;
  }

  const buttonColors = {
    [IntegrationStatus.Stale]: graphSunglow,
    [IntegrationStatus.Expired]: redBase,
    [IntegrationStatus.Operational]: secondaryBase,
  } as Record<IntegrationStatus, string>;

  return (
    <Button
      variant={status === IntegrationStatus.Operational ? 'outline' : 'filled'}
      color={buttonColors[status]}
      disabled={status === IntegrationStatus.Expired && integrationEntry.user_name !== user?.name}
      onClick={onClick}
      style={{
        fontSize: isExpired && !isIntegrator ? 12 : 14,
        width: 145,
        padding: 0,
        height: 32,
      }}
    >
      {getText()}
    </Button>
  );
}
